import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { useEffect, useRef } from 'react';

import { useSurveysGateway } from './SurveysGatewayProvider';
import {
  getCurrentSurveyByGroupQueryOptions,
  getMySurveyResponsesV2QueryOptions,
  hasRespondedToSurvey,
} from '../api/survey';

import { ROUTES } from '@/constants/routes';
import { useAuthResolved } from '@/hooks/useAuthResolved';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { useIsOnline } from '@/hooks/useIsOnline';
import { useUserScope } from '@/hooks/useUserScope';
import { useUserScopedQuery } from '@/hooks/useUserScopedQuery';
import { env } from '@/util/env';
import { isEHRSession } from '@/util/smartLaunch';

/**
 * Prompts the current user to answer the survey configured via
 * `EXPO_PUBLIC_ONBOARDING_SURVEY_GROUP_ID`, at most once per app session. Gated on
 * auth and online status, navigates to the dedicated
 * survey screen, and only fires when
 * the user has no existing response for that survey — the screen itself
 * re-checks this independently on mount (see design.md), so this hook only
 * needs to avoid the common case of re-prompting an already-answered user.
 *
 * Also gates on `useAuthStatus`'s onboarding signals (`needsNpiVerification`,
 * `needsAccountDetailsConfirmation`), the same signal `useOnboardingTutorialModal`
 * uses to withhold its own modal — without it, a user mid-onboarding (pending
 * OAuth, NPI verification, or account details confirmation) could get force-
 * navigated to the survey screen on top of that flow.
 *
 * Resolves the group id to its currently published version before checking
 * responses, since a response's `surveyDefinitionId` is version-specific —
 * comparing against the group id directly would never match and would
 * re-prompt an already-answered user every session.
 */
export function useSurveyPrompt() {
  const surveyGroupId = env.EXPO_PUBLIC_ONBOARDING_SURVEY_GROUP_ID;

  const authStateResolved = useAuthResolved();

  const { canFetch } = useUserScope();

  const isOnline = useIsOnline();

  const { needsNpiVerification, needsAccountDetailsConfirmation } = useAuthStatus();

  const router = useRouter();

  const gateway = useSurveysGateway();

  const promptedRef = useRef(false);

  const enabled =
    !!surveyGroupId &&
    !!authStateResolved &&
    canFetch &&
    !!isOnline &&
    !needsNpiVerification &&
    !needsAccountDetailsConfirmation &&
    !isEHRSession();

  const { data: survey } = useQuery({
    ...getCurrentSurveyByGroupQueryOptions(surveyGroupId ?? '', gateway),
    enabled,
  });

  const { data: responses } = useUserScopedQuery(getMySurveyResponsesV2QueryOptions, [gateway], {
    enabled,
  });

  useEffect(() => {
    if (!surveyGroupId || !survey || !isOnline || promptedRef.current || !responses) {
      return;
    }

    promptedRef.current = true;

    if (!hasRespondedToSurvey(responses, survey.id)) {
      router.push(ROUTES.SURVEY(surveyGroupId));
    }
  }, [surveyGroupId, survey, isOnline, responses, router]);
}
